import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  FloatingLabel,
  Image,
  Spinner,
} from "react-bootstrap";
import apiService from "../../services/apiService";
import { useNotification } from "../../components/notification";
import PhoneVerification from "../../components/phoneVerificationForm";

import logo from "../../logo.png";

// Yup validation schema
const schema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Email is required"),
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .max(20, "Password must be at most 20 characters")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_+{}|:"<>?~`\-=[\]\\;',./])/,
      "Password must contain at least one letter, one number, and one special character"
    ),
  confirmPassword: yup
    .string()
    .required("Confirm password is required")
    .oneOf([yup.ref("password"), null], "Passwords must match"),
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  phone: yup
    .string()
    .required("Phone is required")
    .matches(/^[0-9]{10}$/, "Phone must be a valid 10-digit number"),
  gstNumber: yup.string().notRequired(), // Optional field
});

const Register = () => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema), // Integrate Yup validation
  });
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isPhoneVerified, setPhoneVerified] = useState(false);
  const [phone, setPhone] = useState("");

  // Disable form submission if phone is not verified
  const isFormValid = (data) => {
    return isPhoneVerified && data.phone && data.phone.length === 10;
  };

  const onSubmit = async (data) => {
    if (!isPhoneVerified) {
      showNotification("danger", "Please verify your phone number first.");
      return;
    }

    try {
      setIsLoading(true);
      await apiService.post("/auth/register", data);
      navigate("/confirm-email", { state: { email: data.email } });
    } catch (error) {
      showNotification(
        "danger",
        error.message || error || "Registration failed!",
        5000
      );
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    handlePhoneChange(phone);
  }, [phone]);

  const handlePhoneChange = (value) => {
    setPhone(value);
    setValue("phone", value);
  };

  return (
    <Container className="min-vh-100 d-flex align-items-center justify-content-center flex-column">
      <Row>
        <Col xs={12} className="text-center mb-4">
          <Link to="/">
            <Image
              src={logo}
              alt={`${process.env.REACT_APP_PORTAL}`}
              fluid
              width="225px"
              style={{ cursor: "pointer" }}
            />
          </Link>
        </Col>
        <Col xs={11} md={8} lg={6} xl={6} xxl={6} className="m-auto">
          <Card className="p-4 shadow border-0">
            <Card.Body>
              <h5 className="text-center mb-3">Sign Up</h5>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={6}>
                    <FloatingLabel
                      controlId="txtFirstName"
                      label="First Name"
                      className="mb-4"
                    >
                      <Form.Control
                        type="text"
                        placeholder="First Name"
                        {...register("firstName")}
                      />
                      {errors.firstName && (
                        <Form.Text className="text-danger">
                          {errors.firstName.message}
                        </Form.Text>
                      )}
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel
                      controlId="txtLastName"
                      label="Last Name"
                      className="mb-4"
                    >
                      <Form.Control
                        type="text"
                        placeholder="Last Name"
                        {...register("lastName")}
                      />
                      {errors.lastName && (
                        <Form.Text className="text-danger">
                          {errors.lastName.message}
                        </Form.Text>
                      )}
                    </FloatingLabel>
                  </Col>

                  <Col md={6}>
                    <FloatingLabel
                      controlId="txtEmail"
                      label="Email"
                      className="mb-4"
                    >
                      <Form.Control
                        type="email"
                        placeholder="Email"
                        {...register("email")}
                      />
                      {errors.email && (
                        <Form.Text className="text-danger">
                          {errors.email.message}
                        </Form.Text>
                      )}
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel
                      controlId="txtPassword"
                      label="Password"
                      className="mb-4"
                    >
                      <Form.Control
                        type="password"
                        placeholder="Password"
                        {...register("password")}
                      />
                      {errors.password && (
                        <Form.Text className="text-danger">
                          {errors.password.message}
                        </Form.Text>
                      )}
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel
                      controlId="txtConfirmPassword"
                      label="Confirm Password"
                      className="mb-4"
                    >
                      <Form.Control
                        type="password"
                        placeholder="Confirm Password"
                        {...register("confirmPassword")}
                      />
                      {errors.confirmPassword && (
                        <Form.Text className="text-danger">
                          {errors.confirmPassword.message}
                        </Form.Text>
                      )}
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel
                      controlId="txtGstNumber"
                      label="GST Number (Optional)"
                      className="mb-4"
                    >
                      <Form.Control
                        type="text"
                        placeholder="GST Number"
                        {...register("gstNumber")}
                      />
                    </FloatingLabel>
                  </Col>
                  <Col md={12}>
                    <PhoneVerification
                      value={phone}
                      onChange={handlePhoneChange}
                      onVerified={setPhoneVerified}
                      disabled={isLoading}
                      isExistingVerified={false}
                    />
                    {/* {errors.phone && (
                      <Form.Text className="text-danger">
                        {errors.phone.message}
                      </Form.Text>
                    )} */}
                  </Col>
                </Row>

                <div className="d-grid gap-2">
                  <Button
                    type="submit"
                    className="w-100"
                    disabled={isLoading || !isPhoneVerified}
                  >
                    {isLoading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Creating Account...
                      </>
                    ) : (
                      "REGISTER"
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} className="text-center mt-3">
          <div className="text-dark">
            Already have an account?{" "}
            <Link
              to="/login"
              title="Login"
              className="text-decoration-none link-secondary fw-semibold"
            >
              Login
            </Link>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Register;
